import { useState } from 'react'
import { Package } from 'lucide-react'

interface ServiceLogoProps {
  /** 服务提供商标识 (如 'openai', 'coze', 'dify', 'n8n') - 主要参数 */
  provider: string
  /** 服务名称（用于 alt 文本） */
  name?: string
  /** 尺寸大小 */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl'
  /** 是否半透明 */
  transparent?: boolean
  /** 自定义类名 */
  className?: string
  /** 是否显示在右下角（用于背景装饰） */
  decorative?: boolean
}

// 预定义的服务提供商 logo 映射（基于 public/logos 目录）
const PROVIDER_LOGOS: Record<string, string> = {
  // AI 模型提供商
  'openai': '/logos/openai.jpg',
  'claude': '/logos/claude.jpg',
  'anthropic': '/logos/claude.jpg',
  'gemini': '/logos/gemini.png',
  'google': '/logos/gemini.png',
  'bailian': '/logos/bailian.jpg',

  // 工作流平台
  'coze': '/logos/coze.png',
  'dify': '/logos/dify.png',
  'n8n': '/logos/n8n.webp',

  // 通用服务类型
  'api': '/logos/api.webp',
  'custom': '/logos/custom.jpg',

  // 其他服务（使用在线 favicon）
  'huggingface': 'https://huggingface.co/favicon.ico',
  'replicate': 'https://replicate.com/favicon.ico',
}

// 尺寸映射
const SIZE_CLASSES = {
  xs: 'w-3 h-3',    // 12px - 超小尺寸
  sm: 'w-4 h-4',    // 16px - 用于按钮和下拉框
  md: 'w-6 h-6',    // 24px
  lg: 'w-8 h-8',    // 32px - 大图标
  xl: 'w-12 h-12',  // 48px - 超大图标
  '2xl': 'w-16 h-16', // 64px - 装饰性 logo
}

/**
 * 从 Google Favicon API 获取 logo
 */
function getFaviconUrl(provider: string, size: number = 128): string {
  return `https://www.google.com/s2/favicons?domain=${provider}&sz=${size}`
}

/**
 * 服务提供商 Logo 组件
 *
 * 用于显示服务类型/提供商的品牌 logo，帮助用户快速识别服务类型
 *
 * 用法示例：
 * ```tsx
 * // 在服务卡片上
 * <ServiceLogo provider="coze" size="lg" />
 *
 * // 在筛选按钮上
 * <ServiceLogo provider="dify" size="sm" />
 *
 * // 装饰性背景（半透明）
 * <ServiceLogo provider="n8n" decorative />
 * ```
 */
export function ServiceLogo({
  provider,
  name,
  size = 'md',
  transparent = false,
  className = '',
  decorative = false,
}: ServiceLogoProps) {
  const [imageError, setImageError] = useState(false)
  const [loading, setLoading] = useState(true)

  // 获取 provider logo URL
  const logoUrl = (() => {
    const providerKey = provider.toLowerCase()

    // 1. 直接匹配预定义的 provider logo
    if (PROVIDER_LOGOS[providerKey]) {
      return PROVIDER_LOGOS[providerKey]
    }

    // 2. 提取 provider 的基础名称（去掉后缀如 -workflow, -bot 等）
    // 例如：'n8n-workflow' -> 'n8n', 'coze-bot' -> 'coze'
    const baseProvider = providerKey.split('-')[0]
    if (PROVIDER_LOGOS[baseProvider]) {
      return PROVIDER_LOGOS[baseProvider]
    }

    // 3. 如果都不在预定义列表中，尝试使用 Google Favicon API
    return getFaviconUrl(baseProvider || provider)
  })()

  const displayName = name || provider

  const sizeClass = SIZE_CLASSES[size]
  // decorative 模式强制半透明，否则根据 transparent 属性
  const opacityClass = decorative ? '' : (transparent ? 'opacity-30' : 'opacity-100')
  const decorativeClass = decorative
    ? 'absolute bottom-6 right-6 pointer-events-none opacity-10'
    : ''

  // 如果加载失败，显示默认图标
  if (imageError) {
    return (
      <div
        className={`${sizeClass} ${opacityClass} ${decorativeClass} ${className} flex items-center justify-center bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg`}
      >
        <Package className="w-2/3 h-2/3 text-white" />
      </div>
    )
  }

  return (
    <div className={`${sizeClass} ${opacityClass} ${decorativeClass} ${className} relative flex items-center justify-center`}>
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 rounded-lg">
          <div className="w-1/2 h-1/2 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
        </div>
      )}
      <img
        src={logoUrl}
        alt={`${displayName} logo`}
        className={`${sizeClass} object-contain rounded-lg ${loading ? 'opacity-0' : 'opacity-100'} transition-opacity`}
        onLoad={() => setLoading(false)}
        onError={() => {
          setImageError(true)
          setLoading(false)
        }}
      />
    </div>
  )
}
